import {
  Action,
  Computed,
  computed,
  createContextStore,
  persist,
  State,
  Thunk,
  thunk,
  ThunkOn,
  thunkOn,
} from "easy-peasy";
import _ from "lodash";
import { nfFloat, setFactory } from "./helpers";
import "styled-components/macro";
import { AIRLINE_CODES } from "../../helpers/airline-codes";
import {
  getSkippingFormErrorMessage,
  getSuspendFormErrorMessage,
} from "./form-error-messages";

function extractPrefixedFlightNumbers(data) {
  if (data["carrier_0"] !== undefined) {
    let ret = "";
    ret += prefixFlightno(data, "carrier_0", "flight_number_0");
    if (_.size(data["carrier_1"] || "")) {
      ret += ", ";
      ret += prefixFlightno(data, "carrier_1", "flight_number_1");

      if (_.size(data["carrier_2"] || "")) {
        ret += ", ";
        ret += prefixFlightno(data, "carrier_2", "flight_number_2");
      }
    }

    return ret;
  } else {
    let ret = "";
    ret += prefixFlightno(data, "f1__carrier_0", "f1__flight_number_0");
    if (_.size(data["f1__carrier_1"] || "")) {
      ret += ", ";
      ret += prefixFlightno(data, "f1__carrier_1", "f1__flight_number_1");

      if (_.size(data["f1__carrier_2"] || "")) {
        ret += ", ";
        ret += prefixFlightno(data, "f1__carrier_2", "f1__flight_number_2");
      }
    }
    ret += "; ";
    ret += prefixFlightno(data, "f2__carrier_0", "f2__flight_number_0");
    if (_.size(data["f2__carrier_1"] || "")) {
      ret += ", ";
      ret += prefixFlightno(data, "f2__carrier_1", "f2__flight_number_1");

      if (_.size(data["f2__carrier_2"] || "")) {
        ret += ", ";
        ret += prefixFlightno(data, "f2__carrier_2", "f2__flight_number_2");
      }
    }
    return ret;
  }
}
function prefixFlightno(data, carrierField, flightnoField) {
  return AIRLINE_CODES[data[carrierField]] + " " + data[flightnoField];
}

export interface Reason {
  codeName: string;
  longName: string;
}
export type Reasons = Reason[];

interface Injections {}

export interface SuspendModel {
  notes: string;
  errorMessage: Computed<SuspendModel, null | string, SuspendModel>;
  formData: Computed<
    SuspendModel,
    {
      notes: string;
    },
    SuspendModel
  >;

  setNotes: Action<SuspendModel, string>;
}

export interface BookingModel {
  price: string;
  notes: string;
  flightNumber: string;
  numTickets: number;
  resubscribe: boolean;
  errorMessage: Computed<BookingModel, null | string, BookingModel>;
  formData: Computed<
    BookingModel,
    {
      numTickets: number;
      flightNumber: string;
      price: string;
      notes: string;
      resubscribe: boolean;
    },
    BookingModel
  >;

  setPrice: Action<BookingModel, string>;
  setFlightNumber: Action<BookingModel, string>;
  setNumTickets: Action<BookingModel, number>;
  setNotes: Action<BookingModel, string>;
  setResubscribe: Action<BookingModel, boolean>;
}

export interface SkippingFormData {
  reasons: Reasons;
  flightNumber: null | string;
  numTickets: null | number;
  price: null | string;
  quoteSource: null | string;
  notes: string;
  resubscribe: boolean;
}

export interface SkippingModel {
  reasonsQueryText: string;
  reasons: Reasons;
  flightNumber: null | string;
  numTickets: null | number;
  price: null | string;
  quoteSource: null | string;
  notes: string;
  resubscribe: boolean;
  errorMessage: Computed<SkippingModel, null | string, SkippingModel>;
  formData: Computed<SkippingModel, SkippingFormData, SkippingModel>;

  setReasonsQueryText: Action<SkippingModel, string>;
  setReasons: Action<SkippingModel, Reasons>;
  setPrice: Action<SkippingModel, null | string>;
  setFlightNumber: Action<SkippingModel, null | string>;
  setNumTickets: Action<SkippingModel, null | number>;
  setQuoteSource: Action<SkippingModel, null | string>;
  setNotes: Action<SkippingModel, string>;
  setResubscribe: Action<SkippingModel, boolean>;

  pushReason: Thunk<SkippingModel, Reason, Injections>;
  clearReasons: Thunk<SkippingModel, void, Injections>;
  removeReason: Thunk<SkippingModel, Reason, Injections>;

  onReasonsChange: ThunkOn<SkippingModel, Injections, SkippingModel>;
}

interface SkippingFormStateResolver<T> {
  (s: State<SkippingModel>): T;
}

function factory_resolveSkippingFormData(): [
  SkippingFormStateResolver<Reasons>,
  SkippingFormStateResolver<null | string>,
  SkippingFormStateResolver<null | number>,
  SkippingFormStateResolver<null | string>,
  SkippingFormStateResolver<null | string>,
  SkippingFormStateResolver<string>,
  SkippingFormStateResolver<boolean>
] {
  return [
    (s) => s.reasons,
    (s) => s.flightNumber,
    (s) => s.numTickets,
    (s) => s.price,
    (s) => s.quoteSource,
    (s) => s.notes,
    (s) => s.resubscribe,
  ];
}

const factory_objectifySkippingFormData = () => (
  reasons,
  flightNumber,
  numTickets,
  price,
  quoteSource,
  notes,
  resubscribe
) => ({
  reasons,
  flightNumber,
  numTickets,
  price,
  quoteSource,
  notes,
  resubscribe,
});

function getSkippingModel(
  isFlopptions: boolean,
  flopptionPrice: string,
  flopptionNumTickets: number,
  flopptionFlightNumber: string
): SkippingModel {
  return {
    reasonsQueryText: "",
    reasons: [],
    flightNumber: null,
    numTickets: null,
    price: null,
    quoteSource: null,
    notes: "",
    resubscribe: isFlopptions,
    formData: computed(
      factory_resolveSkippingFormData(),
      factory_objectifySkippingFormData()
    ),
    errorMessage: computed(
      [(s) => s.reasonsQueryText, ...factory_resolveSkippingFormData()],
      (reasonsQueryText, ...formDataPieces) =>
        getSkippingFormErrorMessage(
          factory_objectifySkippingFormData()(...formDataPieces),
          reasonsQueryText
        )
    ),
    setReasons: setFactory<SkippingModel, Reasons>("reasons"),
    pushReason: thunk((actions, newReason, { getState }) => {
      actions.setReasons([...getState().reasons, newReason]);
    }),
    clearReasons: thunk((actions) => {
      actions.setReasons([]);
    }),
    removeReason: thunk((actions, reason, { getState }) => {
      const codeName = _.isString(reason) ? reason : reason.codeName;
      actions.setReasons(
        getState().reasons.filter((r) => r.codeName !== codeName)
      );
    }),
    setReasonsQueryText: setFactory<SkippingModel, string>("reasonsQueryText"),
    setPrice: setFactory<SkippingModel, null | string>("price"),
    setFlightNumber: setFactory<SkippingModel, null | string>("flightNumber"),
    setNumTickets: setFactory<SkippingModel, null | number>("numTickets"),
    setQuoteSource: setFactory<SkippingModel, null | string>("quoteSource"),
    setNotes: setFactory<SkippingModel, string>("notes"),
    setResubscribe: setFactory<SkippingModel, boolean>("resubscribe"),
    //
    onReasonsChange: thunkOn(
      (a) => a.setReasons,
      (actions, target, { getState }) => {
        const mustFillOutQuoteInfo = target.payload
          .map((r) => r.codeName)
          .includes("price");

        const quoteInfoIsNull = getState().price === null;
        if (mustFillOutQuoteInfo) {
          if (quoteInfoIsNull) {
            actions.setPrice(flopptionPrice);
            actions.setFlightNumber(flopptionFlightNumber);
            actions.setNumTickets(flopptionNumTickets);
          }
        } else {
          if (!quoteInfoIsNull) {
            actions.setPrice(null);
            actions.setFlightNumber(null);
            actions.setNumTickets(null);
            actions.setQuoteSource(null);
          }
        }
      }
    ),
  };
}

function getBookingModel(
  flopptionPrice: string,
  flopptionNumTickets: number,
  flopptionFlightNumber: string
): BookingModel {
  return {
    price: flopptionPrice,
    numTickets: flopptionNumTickets,
    flightNumber: flopptionFlightNumber,
    notes: "",
    resubscribe: false,
    formData: computed(
      ({ price, numTickets, flightNumber, notes, resubscribe }) => ({
        price,
        numTickets,
        flightNumber,
        notes,
        resubscribe,
      })
    ),
    errorMessage: null,
    // dataIsSubmittable: computed(({ price, numTickets, flightNumber, notes }) => (
    //   price &&
    //   numTickets &&
    //   flightNumber &&
    //   notes
    // )),
    setPrice: setFactory<BookingModel, string>("price"),
    setNumTickets: setFactory<BookingModel, number>("numTickets"),
    setFlightNumber: setFactory<BookingModel, string>("flightNumber"),
    setNotes: setFactory<BookingModel, string>("notes"),
    setResubscribe: setFactory<BookingModel, boolean>("resubscribe"),
  };
}

function getSuspendModel(): SuspendModel {
  return {
    notes: "",
    formData: computed([(s) => s.notes], (notes) => ({
      notes,
    })),
    errorMessage: computed(getSuspendFormErrorMessage),
    setNotes: setFactory<SuspendModel, string>("notes"),
  };
}

function model({ key, actionName, opportunityData, isFlopptions }) {
  let ret = {};
  const flopptionPrice = _.toString(nfFloat.format(opportunityData["fare"]));
  const flopptionNumTickets: number = opportunityData["num_passengers"];
  const flopptionflightNumber = extractPrefixedFlightNumbers(opportunityData);
  if (actionName === "booked") {
    ret = getBookingModel(
      flopptionPrice,
      flopptionNumTickets,
      flopptionflightNumber
    );
  } else if (actionName === "skipping") {
    ret = getSkippingModel(
      isFlopptions,
      flopptionPrice,
      flopptionNumTickets,
      flopptionflightNumber
    );
  } else if (actionName === "suspend") {
    ret = getSuspendModel();
  }

  ret = { ...ret, dataIsSubmittable: true, isFlopptions };

  return persist({ [key]: ret });
}

export const FormStore = createContextStore(model);
// export const useFormStore = createComponentStore(model);
